import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Users, Play, Square, Sparkles, Share2, LogIn } from 'lucide-react';
import io from 'socket.io-client';
import PaywallOverlay from '@/components/PaywallOverlay';
import { PollarBrandMini } from '@/components/PollarBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const getWsUrl = () => {
  if (BACKEND_URL && BACKEND_URL.startsWith('http')) {
    return BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  }
  // If relative or empty, use window location
  const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${protocol}${window.location.host}`;
};
const WS_URL = getWsUrl();

const FacilitatorMain = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterMessages, setClusterMessages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  const copyShareLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard!');
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  const [gradientBg, setGradientBg] = useState('');
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const sessionRef = useRef(null);

  // Keep ref synced with session state for polling
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Generate random gradient background for setup phase
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Update localStorage to ensure fresh data across app
      localStorage.setItem('facilitator', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  useEffect(() => {
    const gradients = [
      'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)', // dark blue
      'linear-gradient(135deg, #064e3b 0%, #065f46 100%)', // dark green
      'linear-gradient(135deg, #581c87 0%, #6b21a8 100%)', // dark purple
      'linear-gradient(135deg, #831843 0%, #9f1239 100%)', // dark pink
      'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)', // dark red
      'linear-gradient(135deg, #713f12 0%, #854d0e 100%)', // dark yellow/gold
      'linear-gradient(135deg, #1e293b 0%, #334155 100%)', // dark slate
    ];
    setGradientBg(gradients[Math.floor(Math.random() * gradients.length)]);
    
    // Fetch profile
    fetchProfile();
  }, []);

  useEffect(() => {
    fetchSession(true); // Initial load
    fetchMessages();
    connectWebSocket('facilitator');

    // Poll for participant count and messages every 3 seconds
    // Reduced polling interval for better performance (WebSocket handles real-time updates)
    const pollInterval = setInterval(() => {
      fetchSession(false); // Fallback polling for session status
      // Messages come via WebSocket, no need to poll
    }, 10000); // 10 seconds (was 3 seconds)

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      clearInterval(pollInterval);
    };
  }, [sessionId]);

    // Start timer when session becomes active
    useEffect(() => {
      if (session && session.status === 'active' && !timerRef.current && timeLeft > 0) {
        console.log('Starting countdown timer, timeLeft:', timeLeft);
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            // Ensure we don't go below 0
            const nextTime = prev - 1;
            if (nextTime <= 0) {
              clearInterval(timerRef.current);
              timerRef.current = null;
              // Only end session if we hit exactly 0 from a positive number
              if (prev > 0) {
                endSession();
              }
              return 0;
            }
            return nextTime;
          });
        }, 1000);
      }
  
      return () => {
        // Do NOT clear interval here if session status hasn't changed to something other than active
        // This prevents timer reset on minor re-renders
        if (session?.status !== 'active' && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [session?.status]); // Remove timeLeft dependency to avoid re-creating timer on every tick

  useEffect(() => {
    if (session && canvasRef.current) {
      console.log('Canvas redraw triggered, messages:', messages.length);
      initCanvas();
    }

    // Handle window resize for full-screen canvas
    const handleResize = () => {
      if (session && session.status === 'active' && canvasRef.current) {
        initCanvas();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [session, messages, messages.length]);

  const fetchSession = async (isInitialLoad = false) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/sessions/${sessionId}`, { headers });
      
      // Check if user is authenticated
      if (!token) {
        setIsLoggedIn(false);
      }
      
      // Prevent reverting to 'setup' if we are already 'active' or 'completed'
      // This handles race conditions where a poll returns stale 'setup' status after we've started
      const currentSession = sessionRef.current;
      if (currentSession && 
          (currentSession.status === 'active' || currentSession.status === 'completed') && 
          response.data.status === 'setup') {
        console.log('Ignoring stale session data (setup) while active/completed');
        return;
      }

      setSession(response.data);
      setParticipantCount(response.data.participant_count || 0);

      // Calculate remaining time if session is active
      if (response.data.status === 'active' && response.data.started_at) {
        let remaining;
        
        // If using end_datetime, calculate from end time
        if (response.data.end_datetime) {
          const endTime = new Date(response.data.end_datetime).getTime();
          const now = Date.now();
          remaining = Math.max(0, Math.floor((endTime - now) / 1000));
          
          console.log('Time sync (end_datetime):', {
            endTime: response.data.end_datetime,
            remaining,
            method: 'calculated from end_datetime'
          });
        } else {
          // Calculate from start time + time_limit
          const startedAt = new Date(response.data.started_at);
          const now = new Date();
          const elapsedSeconds = Math.floor((now - startedAt) / 1000);
          remaining = Math.max(0, response.data.time_limit - elapsedSeconds);
          
          console.log('Time sync (time_limit):', {
            startedAt: startedAt.toISOString(),
            elapsed: elapsedSeconds,
            remaining,
            timeLimit: response.data.time_limit
          });
        }
        
        setTimeLeft(remaining);
        
        // If time ran out naturally, end the session
        if (remaining === 0 && !timerRef.current) {
          endSession();
        }
      }

      // If session is completed, try to fetch existing summary
      if (response.data.status === 'completed') {
        try {
          const token = localStorage.getItem('token');
          const summaryResponse = await axios.get(`${API}/sessions/${sessionId}/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSummary(summaryResponse.data);
        } catch (err) {
          console.log('No summary found yet');
        }
      }
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/messages`);
      const newMessages = response.data;
      
      // Always update to ensure canvas redraws with latest messages
      if (JSON.stringify(newMessages) !== JSON.stringify(messages)) {
        console.log('Messages updated, count:', newMessages.length);
        setMessages(newMessages);
      }
    } catch (error) {
      console.error('Failed to load messages', error);
    }
  };

  const connectWebSocket = (role = 'viewer') => {
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const wsUrl = `${protocol}${window.location.host}/api/ws/${sessionId}?role=${role}`;
    console.log('Facilitator connecting to WebSocket:', wsUrl);
    console.log('BACKEND_URL:', BACKEND_URL);
    console.log('WS_URL:', WS_URL);
    const ws = new WebSocket(wsUrl);
    let pingInterval;

    ws.onopen = () => {
      console.log('✓ Facilitator WebSocket connected successfully');
      // Send ping every 30 seconds to keep connection alive
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data);

      if (data.type === 'new_message') {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === data.message.id)) {
            return prev;
          }
          return [...prev, data.message];
        });
      } else if (data.type === 'new_vote') {
        // Update votes in real-time for facilitator view
        setMessages(prev => prev.map(msg => 
          msg.id === data.message_id 
            ? { ...msg, hearts: data.hearts, hearted_by: data.hearted_by }
            : msg
        ));
      } else if (data.type === 'participant_count') {
        console.log('Updating participant count to:', data.count);
        setParticipantCount(data.count);
      } else if (data.type === 'session_started') {
        toast.success('Session started!');
      } else if (data.type === 'session_ended') {
        toast.info('Session ended!');
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (pingInterval) {
        clearInterval(pingInterval);
      }
    };

    wsRef.current = ws;
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw dark multicolor gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#0f0c29');    // deep purple
    gradient.addColorStop(0.4, '#302b63');  // purple-blue
    gradient.addColorStop(0.7, '#24243e');  // dark blue
    gradient.addColorStop(1, '#0f0c29');    // deep purple
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw distant moon (vector)
    drawMoon(ctx, canvas);
    
    // Draw stars on top
    const messageCount = messages.length;
    if (messageCount > 0) {
      drawStars(ctx, canvas, messageCount);
    }
  };

  const drawMoon = (ctx, canvas) => {
    // Position moon in upper right quadrant
    const moonX = canvas.width * 0.75;
    const moonY = canvas.height * 0.25;
    const moonRadius = Math.min(canvas.width, canvas.height) * 0.08;

    // Moon glow
    const glowGradient = ctx.createRadialGradient(moonX, moonY, moonRadius * 0.5, moonX, moonY, moonRadius * 2);
    glowGradient.addColorStop(0, 'rgba(255, 255, 200, 0.1)');
    glowGradient.addColorStop(0.5, 'rgba(255, 255, 200, 0.05)');
    glowGradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
    ctx.fillStyle = glowGradient;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonRadius * 2, 0, Math.PI * 2);
    ctx.fill();

    // Moon body
    const moonGradient = ctx.createRadialGradient(moonX - moonRadius * 0.3, moonY - moonRadius * 0.3, moonRadius * 0.2, moonX, moonY, moonRadius);
    moonGradient.addColorStop(0, '#f9fafb');
    moonGradient.addColorStop(0.7, '#e5e7eb');
    moonGradient.addColorStop(1, '#d1d5db');
    ctx.fillStyle = moonGradient;
    ctx.beginPath();
    ctx.arc(moonX, moonY, moonRadius, 0, Math.PI * 2);
    ctx.fill();

    // Moon craters (simple circles)
    ctx.fillStyle = 'rgba(200, 200, 200, 0.3)';
    const craters = [
      { x: moonX - moonRadius * 0.3, y: moonY - moonRadius * 0.2, r: moonRadius * 0.15 },
      { x: moonX + moonRadius * 0.2, y: moonY + moonRadius * 0.3, r: moonRadius * 0.2 },
      { x: moonX + moonRadius * 0.4, y: moonY - moonRadius * 0.4, r: moonRadius * 0.1 },
      { x: moonX - moonRadius * 0.5, y: moonY + moonRadius * 0.4, r: moonRadius * 0.12 }
    ];
    craters.forEach(crater => {
      ctx.beginPath();
      ctx.arc(crater.x, crater.y, crater.r, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw Pollar logo in the center of the moon
    const logo = new Image();
    logo.src = '/assets/logo/pollar.svg';
    logo.onload = () => {
      const logoSize = moonRadius * 0.8;
      ctx.drawImage(logo, moonX - logoSize / 2, moonY - logoSize / 2, logoSize, logoSize);
    };
  };

  const drawStars = (ctx, canvas, count) => {
    // Use seeded random positions for consistent star placement
    for (let i = 0; i < count; i++) {
      // Use message index as seed for consistent position
      const seed = i * 12345;
      const x = ((seed * 9301 + 49297) % 233280) / 233280 * canvas.width;
      const y = ((seed * 1103 + 7919) % 233280) / 233280 * (canvas.height * 0.7);
      const size = 2 + ((seed % 3) * 0.5);

      // Glow effect
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#ffffff';
      
      // Star color - mix of white and yellow
      const colors = ['#ffffff', '#fef08a', '#fde047', '#fbbf24'];
      ctx.fillStyle = colors[i % colors.length];
      
      // Draw star shape
      drawStar(ctx, x, y, 5, size, size * 0.5);
      
      ctx.shadowBlur = 0;
    }
  };

  const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
    let rot = Math.PI / 2 * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    
    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
  };

  const startSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/sessions/${sessionId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const startedAt = response.data.started_at;
      setSession({ ...session, status: 'active', started_at: startedAt });
      
      // Calculate time limit based on end_datetime or use fixed time_limit
      let calculatedTimeLimit;
      if (session.end_datetime) {
        // Calculate seconds until end_datetime
        const endTime = new Date(session.end_datetime).getTime();
        const now = Date.now();
        calculatedTimeLimit = Math.max(0, Math.floor((endTime - now) / 1000));
        console.log('Using end_datetime, calculated time:', calculatedTimeLimit, 'seconds');
      } else {
        calculatedTimeLimit = session.time_limit;
      }
      
      setTimeLeft(calculatedTimeLimit);

      // Start countdown
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            endSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast.success('Session started!');
    } catch (error) {
      toast.error('Failed to start session');
    }
  };

  const endSession = async () => {
    try {
      // Show processing animation immediately
      setProcessing(true);
      
      const token = localStorage.getItem('token');
      await axios.put(`${API}/sessions/${sessionId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSession({ ...session, status: 'completed' });
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      toast.success('Session ended! Processing ideas...');
      
      // Fetch final messages before processing (crucial for timer-based endings)
      console.log('Fetching final messages before processing...');
      await fetchMessages();
      
      // Small delay to ensure messages are updated in state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Automatically process and summarize
      await processSummary();
    } catch (error) {
      toast.error('Failed to end session');
      setProcessing(false);
    }
  };

  const processSummary = async () => {
    setProcessing(true);
    
    try {
      // Fetch latest messages from server before checking
      const messagesResponse = await axios.get(`${API}/sessions/${sessionId}/messages`);
      const latestMessages = messagesResponse.data;
      console.log('Latest messages count:', latestMessages.length);
      
      if (latestMessages.length === 0) {
        toast.error('No messages to process');
        setProcessing(false);
        return;
      }
      
      // Update messages in state
      setMessages(latestMessages);

      const token = localStorage.getItem('token');
      console.log('Starting AI processing with', latestMessages.length, 'messages...');
      const response = await axios.post(
        `${API}/sessions/${sessionId}/process`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✓ AI processing complete', response.data);
      setSummary(response.data);
      
      // Keep animation visible for a moment while UI renders
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProcessing(false);
      toast.success('Ideas clustered and summarized!');
    } catch (error) {
      console.error('Processing error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to process ideas. Please try again.';
      toast.error(errorMsg);
      setProcessing(false);
    }
  };

  const viewClusterMessages = (cluster) => {
    setSelectedCluster(cluster);
    // Filter messages that belong to this cluster
    const clusterMsgs = messages.filter(msg => cluster.message_ids.includes(msg.id));
    setClusterMessages(clusterMsgs);
  };

  const getUserStats = () => {
    const stats = {};
    messages.forEach(msg => {
      const name = msg.sender_name || 'Anonymous';
      stats[name] = (stats[name] || 0) + 1;
    });
    
    // Sort by count descending and take top 10
    return Object.entries(stats)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  };

  // Format time for active session countdown
  const formatTimeCountdown = (seconds) => {
    if (seconds >= 86400) {
      // More than 24 hours: "X days, HH:MM:SS"
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${days} ${days === 1 ? 'day' : 'days'}, ${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      // Less than 24 hours: "HH:MM:SS"
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Format time for lobby display (before session starts)
  const formatLobbyTime = () => {
    if (session.end_datetime) {
      // If using end date/time, show as "dd mmm YY, HH:MM"
      const endDate = new Date(session.end_datetime);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = endDate.getDate().toString().padStart(2, '0');
      const month = months[endDate.getMonth()];
      const year = endDate.getFullYear().toString().slice(-2);
      const hours = endDate.getHours().toString().padStart(2, '0');
      const minutes = endDate.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } else {
      // If using time limit, show as "HH:MM:SS"
      const hours = Math.floor(session.time_limit / 3600);
      const mins = Math.floor((session.time_limit % 3600) / 60);
      const secs = session.time_limit % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  if (!session) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <>
      
      {/* Setup Phase - Full Screen Waiting Room */}
      {session.status === 'setup' && (
        <div className="fixed inset-0" style={{ background: gradientBg }}>
          {/* Top Bar */}
          <div className="fixed top-0 left-0 right-0 z-30 bg-black/20 backdrop-blur-sm border-b border-white/10 px-6 py-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              {/* Left: Brand and Back button + Participant count */}
              <div className="flex items-center gap-4 flex-1">
                <PollarBrandMini className="text-white" />
                <Button
                  onClick={() => {
                    const referrer = sessionStorage.getItem('referrer') || '/dashboard';
                    navigate(referrer);
                  }}
                  variant="ghost"
                  className="text-white hover:bg-white/10"
                  data-testid="back-to-dashboard-setup"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Dashboard
                </Button>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Users className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white" data-testid="participant-count-setup">{participantCount}</span>
                </div>
              </div>

              {/* Center: Timer */}
              <div className="flex-1 flex justify-center">
                <div className="text-5xl font-bold text-white" data-testid="timer-setup">
                  {formatLobbyTime()}
                </div>
              </div>

              {/* Right: Start button */}
              <div className="flex-1 flex justify-end">
                <Button
                  onClick={startSession}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
                  data-testid="start-session-button-setup"
                >
                  <Play className="mr-2 h-6 w-6" />
                  Start Session
                </Button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex flex-col items-center justify-center h-full pt-24">
            {/* Title */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-12 px-8 text-center max-w-5xl" style={{ textShadow: '3px 3px 12px rgba(0,0,0,0.5)' }} data-testid="session-topic-setup">
              {session.topic}
            </h1>

            {/* Large QR Code */}
            {session.qr_code_data && (
              <div className="bg-white p-8 rounded-3xl shadow-2xl" style={{ width: '20vw', minWidth: '280px' }}>
                <p className="text-center text-lg text-gray-700 mb-4 font-semibold">Scan to Join</p>
                <img src={session.qr_code_data} alt="QR Code" className="w-full h-auto" data-testid="qr-code-setup" />
                <p className="text-center text-sm text-gray-500 mt-4">Waiting for participants...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Session Phase */}
      {session.status === 'active' && (
        <>
      {/* Main Canvas Area */}
      <div className="fixed inset-0 overflow-hidden">
          {/* Top Bar */}
          <div className={`${session.status === 'active' ? 'fixed' : 'absolute'} top-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-sm border-b px-6 py-4 flex items-center`}>
            {/* Left: Brand, Topic and Participant Count */}
            <div className="flex-1 flex items-center gap-4">
              <PollarBrandMini />
              
              {/* Session Topic */}
              <div className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-2 rounded-full border border-purple-200">
                <span className="font-semibold text-gray-800 max-w-xs truncate">
                  {session.topic}
                </span>
              </div>
              
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <Users className="w-5 h-5 text-gray-600" />
                <span className="font-semibold" data-testid="participant-count">{participantCount}</span>
              </div>
            </div>

            {/* Center: Timer */}
            <div className="flex-1 flex justify-center">
              <div className="text-4xl font-bold text-indigo-600" data-testid="timer-display">
                {formatTimeCountdown(timeLeft)}
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex-1 flex justify-end gap-2">
              {session.status === 'setup' && (
                <Button
                  onClick={startSession}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 rounded-xl"
                  data-testid="start-session-button"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Start
                </Button>
              )}
              {session.status === 'active' && (
                <Button
                  onClick={endSession}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 rounded-xl"
                  data-testid="end-session-button"
                >
                  <Square className="mr-2 h-5 w-5" />
                  End
                </Button>
              )}
              {session.status === 'completed' && !summary && (
                <Button
                  onClick={processSummary}
                  disabled={processing || messages.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 rounded-xl"
                  data-testid="process-button"
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  {processing ? 'Processing...' : 'Cluster & Summarize'}
                </Button>
              )}
            </div>
          </div>

          {/* Canvas */}
          <canvas
            ref={canvasRef}
            className="fixed inset-0 w-full h-full"
          />

          {/* Topic Display */}
          {session.status === 'active' && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-20 max-w-4xl -mt-20">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 px-8" style={{ textShadow: '3px 3px 8px rgba(0,0,0,0.6)' }} data-testid="session-topic">
                {session.topic}
              </h1>
              {/* QR Code below topic when active */}
              {session.qr_code_data && (
                <div className="inline-block bg-white/90 backdrop-blur-sm p-4 rounded-2xl shadow-2xl">
                  <p className="text-center text-xs text-gray-600 mb-2">Scan to join</p>
                  <img src={session.qr_code_data} alt="QR Code" className="w-40 h-40 mx-auto" data-testid="qr-code-active" />
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {/* Completed Session Layout */}
      {session.status === 'completed' && (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          {/* Top Bar with Back Button, Share Button, and QR Code */}
          <div className="bg-white border-b shadow-sm sticky top-0 z-40">
            <div className="container mx-auto px-4 py-4 max-w-7xl flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => {
                    const referrer = sessionStorage.getItem('referrer') || '/dashboard';
                    navigate(isLoggedIn ? referrer : '/login');
                  }}
                  variant="ghost"
                  data-testid="back-to-dashboard-completed"
                >
                  {isLoggedIn ? (
                    <>
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      Back
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-5 w-5" />
                      Login / Sign Up
                    </>
                  )}
                </Button>
                
                {/* Share Link Button */}
                <Button
                  onClick={copyShareLink}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Share2 className="h-4 w-4" />
                  Share Link
                </Button>
              </div>
              
              {/* QR Code with Hover Zoom */}
              {session.qr_code_data && (
                <div className="relative group">
                  <div className="bg-white p-2 rounded-lg border-2 border-gray-200 cursor-pointer transition-all hover:border-indigo-500">
                    <img 
                      src={session.qr_code_data} 
                      alt="QR Code" 
                      className="w-16 h-16 object-contain"
                      style={{ imageRendering: 'pixelated' }}
                    />
                  </div>
                  {/* Enlarged QR on Hover */}
                  <div className="absolute top-full right-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none group-hover:pointer-events-auto">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl border-2 border-indigo-500">
                      <p className="text-center text-sm text-gray-700 mb-3 font-semibold">Scan to View Results</p>
                      <div className="bg-white flex items-center justify-center" style={{ width: '280px', height: '280px' }}>
                        <img 
                          src={session.qr_code_data} 
                          alt="QR Code Large" 
                          className="w-full h-full object-contain"
                          style={{ imageRendering: 'pixelated', maxWidth: '280px', maxHeight: '280px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary Content */}
          {summary && (
            <div className="container mx-auto px-4 py-8 max-w-7xl space-y-8">
              {/* Session Topic Header */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border-2 border-purple-200">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">{session.topic}</h1>
                  <div className="flex items-center justify-center gap-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span>{participantCount} participants</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <span>{messages.length} ideas</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 1. Session Summary with User Count */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-3 rounded-xl">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h2 className="text-3xl font-bold">Session Summary</h2>
                  </div>
                  {/* User Count Badge */}
                  <div className="flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-2 rounded-full border-2 border-indigo-200">
                    <Users className="w-5 h-5 text-indigo-600" />
                    <span className="font-bold text-indigo-700">{participantCount}</span>
                    <span className="text-sm text-indigo-600">participants</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border-l-4 border-indigo-500">
                  <p className="text-gray-800 text-lg leading-relaxed">{summary.summary}</p>
                </div>
              </div>

            {/* 2. User Contribution Chart */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-3 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold">Top Contributors</h3>
              </div>
              <div className="space-y-3">
                {getUserStats().map(([user, count], index) => {
                  const maxCount = getUserStats()[0][1];
                  const percentage = (count / maxCount) * 100;
                  return (
                    <div key={user} className="flex items-center gap-4">
                      <div className="w-8 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-400 text-orange-900' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-semibold text-gray-800">{user}</span>
                          <span className="text-sm font-medium text-gray-600">{count} {count === 1 ? 'idea' : 'ideas'}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              index === 0 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                              index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
                              index === 2 ? 'bg-gradient-to-r from-orange-400 to-red-500' :
                              'bg-gradient-to-r from-indigo-400 to-purple-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 3. Idea Clusters */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold">Idea Clusters</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...summary.clusters]
                  .sort((a, b) => b.message_count - a.message_count)
                  .map((cluster, index) => {
                    const scale = Math.min(1 + cluster.message_count * 0.02, 1.2);
                    return (
                      <Card
                        key={cluster.id}
                        className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-indigo-500 relative overflow-hidden"
                        onClick={() => viewClusterMessages(cluster)}
                        data-testid={`cluster-${cluster.id}`}
                      >
                        <div className="absolute top-2 right-2">
                          <span className="bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                            #{index + 1}
                          </span>
                        </div>
                        <CardContent className="p-6" style={{ minHeight: `${100 * scale}px` }}>
                          <h4 className="font-bold text-xl mb-3 text-indigo-900 pr-8">{cluster.title}</h4>
                          <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                            {cluster.message_count} {cluster.message_count === 1 ? 'idea' : 'ideas'}
                          </Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </div>

            {/* 4. Idea Ranking by Hearts - Paywalled for Free Users */}
            <PaywallOverlay
              isPaid={profile?.is_paid}
              message="Upgrade to Premium to view voting results and see which ideas your participants loved most"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-r from-pink-600 to-red-600 p-3 rounded-xl">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold">Top Voted Ideas</h3>
                  <span className="bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1 rounded-full">Live Voting</span>
                </div>
                
                <div className="space-y-3">
                {messages.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No ideas submitted yet</p>
                ) : (
                  [...messages]
                    .sort((a, b) => (b.hearts || 0) - (a.hearts || 0))
                    .slice(0, 10)
                    .map((msg, index) => (
                      <div
                        key={msg.id}
                        className="bg-gradient-to-r from-pink-50 to-red-50 rounded-xl p-4 border-l-4 border-pink-500 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700' :
                            index === 2 ? 'bg-gradient-to-br from-orange-400 to-red-500 text-white' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {index + 1}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-pink-700">{msg.sender_name}</span>
                              <span className="text-xs text-gray-500">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-gray-800 text-sm leading-relaxed">{msg.content}</p>
                          </div>

                          <div className="flex items-center gap-1 px-3 py-2 bg-white rounded-lg shadow-sm">
                            <svg className="w-5 h-5 text-red-500 fill-current" viewBox="0 0 24 24">
                              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            <span className="font-bold text-gray-900">{msg.hearts || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
            </PaywallOverlay>

              {/* Cluster Messages Modal */}
              {selectedCluster && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedCluster(null)}>
                  <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-2xl font-bold mb-2">{selectedCluster.title}</h3>
                          <p className="text-indigo-100">{selectedCluster.message_count} {selectedCluster.message_count === 1 ? 'idea' : 'ideas'} in this cluster</p>
                        </div>
                        <button
                          onClick={() => setSelectedCluster(null)}
                          className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                          data-testid="close-cluster-modal"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-6 overflow-y-auto max-h-[calc(80vh-150px)]">
                      <div className="space-y-4">
                        {clusterMessages.map((msg, idx) => (
                          <div key={msg.id} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors" data-testid={`cluster-message-${idx}`}>
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-semibold text-indigo-600">{msg.sender_name}</span>
                              <span className="text-sm text-gray-500">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-gray-800 leading-relaxed">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Processing Loader - Global Overlay (Always on top) */}
      {processing && (
        <div className="fixed inset-0 bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-blue-900/95 backdrop-blur-md flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="text-center">
            {/* Spinning Brain Animation */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              {/* Outer rotating circle */}
              <div className="absolute inset-0 border-4 border-purple-400/30 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
              
              {/* Middle rotating circle */}
              <div className="absolute inset-2 border-4 border-indigo-400/40 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
              
              {/* Inner rotating circle */}
              <div className="absolute inset-4 border-4 border-blue-400/50 rounded-full animate-spin" style={{ animationDuration: '1.5s' }}></div>
              
              {/* Center sparkles icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-12 h-12 text-white animate-pulse" />
              </div>
              
              {/* Orbiting dots */}
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
                <div className="absolute top-0 left-1/2 w-3 h-3 bg-purple-400 rounded-full -translate-x-1/2 shadow-lg shadow-purple-400/50"></div>
              </div>
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                <div className="absolute top-0 left-1/2 w-3 h-3 bg-indigo-400 rounded-full -translate-x-1/2 shadow-lg shadow-indigo-400/50"></div>
              </div>
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s', animationDelay: '2s' }}>
                <div className="absolute top-0 left-1/2 w-3 h-3 bg-blue-400 rounded-full -translate-x-1/2 shadow-lg shadow-blue-400/50"></div>
              </div>
            </div>

            {/* Loading Text */}
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-white animate-pulse">
                AI is Analyzing Ideas...
              </h2>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
              <p className="text-white/80 text-lg">
                Clustering ideas and generating insights
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FacilitatorMain;